import { Loader2, ImagePlus, Trash2 } from 'lucide-react';
import { photoUploadError } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import { FileDropZone } from '@/components/shared/FileDropZone';
import { useToast } from '@/hooks/use-toast';
import { useDeleteLocationPhoto, useUploadLocationPhoto } from '@/hooks/useLocations';

/**
 * Upload / replace / remove the one photo a service address carries.
 *
 * Shared by the address page and the address editor so both behave identically —
 * the editor is where people expect to change an address, the page is where they
 * see it. `compact` is the editor's tighter layout, nothing more.
 */
export function AddressPhotoField({
  locationId,
  photoUrl,
  compact = false,
}: {
  locationId: string;
  photoUrl?: string | null;
  compact?: boolean;
}) {
  const { toast } = useToast();
  const upload = useUploadLocationPhoto(locationId);
  const remove = useDeleteLocationPhoto(locationId);

  const handleFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    // Same rule the backend enforces, so the user hears about it before the
    // round trip rather than after it.
    const error = photoUploadError(file.type, file.size, file.name);
    if (error) {
      toast({ title: 'Cannot use that file', description: error, variant: 'destructive' });
      return;
    }

    upload.mutate(file);
  };

  const busyContent = (
    <span className="flex items-center justify-center text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin mr-2" />
      Uploading…
    </span>
  );

  if (photoUrl) {
    return (
      <div className={compact ? 'flex gap-3 items-start' : 'flex flex-col sm:flex-row gap-4'}>
        <img
          src={photoUrl}
          alt="The building at this address"
          className={
            compact
              ? 'w-32 aspect-[4/3] object-cover rounded-md border border-gray-200'
              : 'w-full sm:w-80 aspect-[4/3] object-cover rounded-lg border border-gray-200'
          }
        />
        <div className="flex-1 flex flex-col gap-2">
          <FileDropZone
            onFiles={handleFiles}
            accept="image/*"
            multiple={false}
            busy={upload.isPending}
            className={
              compact
                ? 'flex-1 !p-4 flex items-center justify-center'
                : 'flex-1 flex items-center justify-center'
            }
            busyContent={busyContent}
          >
            <span className="text-sm text-gray-600">
              {compact ? 'Drop or click to replace' : 'Drop a new photo here, or click to replace'}
            </span>
          </FileDropZone>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => remove.mutate()}
            disabled={remove.isPending || upload.isPending}
          >
            {remove.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Remove
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FileDropZone
      onFiles={handleFiles}
      accept="image/*"
      multiple={false}
      busy={upload.isPending}
      className={compact ? '!p-5' : ''}
      busyContent={busyContent}
    >
      <span className="flex flex-col items-center text-sm text-gray-600">
        <ImagePlus className={compact ? 'h-6 w-6 text-gray-400 mb-1' : 'h-8 w-8 text-gray-400 mb-2'} />
        {compact
          ? 'Drop a photo of the building, or click to browse'
          : 'Drop a photo of the building here, or click to browse'}
      </span>
    </FileDropZone>
  );
}
